/**
 * Job Posting Data Models & Interfaces
 * Synchronized with backend DTOs
 */

import { ScopeOfPracticeEnum } from '@umss/shared/utils';
import {
  JobPositionTypeEnum,
  JobExperienceLevelEnum,
  JobPostingStatusEnum,
  JobClosureOutcomeEnum
} from '@umss/shared/data-access';

/* ============================================
   FACILITY DTO
   ============================================ */

export interface FacilityDto {
  id: string;
  name: string;
  location: string;
  fullAddress: string;
}

/* ============================================
   PRICE RANGE
   ============================================ */

export interface PriceRangeDto {
  minHourlyRate: number;
  maxHourlyRate: number;
}

/* ============================================
   JOB POSTING DTOs
   ============================================ */

/**
 * Job Posting - List Item Response
 * Returned in listing (GET /api/job-postings/facility/{facilityId})
 */
export interface JobPostingListItemDto {
  id: string;
  facility: FacilityDto;
  postedAt: string | null;
  scopeOfPractice: ScopeOfPracticeEnum;
  scopeOfPracticeDisplay: string;
  positionType: JobPositionTypeEnum;
  positionTypeDisplay: string;
  experienceLevel: JobExperienceLevelEnum;
  experienceLevelDisplay: string;
  lifecycleStage: JobPostingStatusEnum;
  departmentId?: number | null;
  department: string | null;
  priceRange: PriceRangeDto;
  interestedAt?: string | null;
  description?: string | null;
  closedAt?: string | null;
  closureOutcome?: JobClosureOutcomeEnum | null;
  closureOutcomeDisplay?: string | null;
  closureNotes?: string | null;
  interestedCount: number;
}

/**
 * Job Posting - Detail Response
 * Returned by GET /api/job-postings/{id}/detail
 * Same structure as list, used for facility admin viewing full posting details
 */
export interface JobPostingDetailDto extends JobPostingListItemDto {}

/**
 * Job Posting - Paginated List Response
 * Returned by GET /api/job-postings/facility/{facilityId}
 */
export interface JobPostingPaginatedListDto {
  items: JobPostingListItemDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/* ============================================
   CREATE JOB POSTING REQUEST
   ============================================ */

export interface CreateJobPostingRequestDto {
  facilityId: string;
  scopeOfPractice: ScopeOfPracticeEnum;
  positionType: JobPositionTypeEnum;
  experienceLevel: JobExperienceLevelEnum;
  minHourlyRate: number | null;
  maxHourlyRate: number | null;
  description: string | null;
  departmentId?: number | null; // Facility subtype ID (changed from department string)
  publishImmediately: boolean;
}

/* ============================================
   UPDATE JOB POSTING REQUEST
   ============================================ */

export interface UpdateJobPostingRequestDto {
  scopeOfPractice?: ScopeOfPracticeEnum | null;
  positionType?: JobPositionTypeEnum | null;
  experienceLevel?: JobExperienceLevelEnum | null;
  minHourlyRate?: number | null;
  maxHourlyRate?: number | null;
  description?: string | null;
  departmentId?: number | null; // Facility subtype ID (changed from department string)
}

/* ============================================
   CLOSE JOB POSTING REQUEST
   ============================================ */

export interface CloseJobPostingRequestDto {
  outcome: JobClosureOutcomeEnum;
  hiredContractorId?: string | null;
  // Pending backend field — sent when outcome === 'Other'
  notes?: string | null;
}

/* ============================================
   INTERESTED CONTRACTOR
   ============================================ */

export interface InterestedContractorDto {
  contractorId: string;
  name: string;
  profilePictureUrl: string | null;
  scopeOfPractice: ScopeOfPracticeEnum;
  scopeOfPracticeDisplay: string;
  yearsExperience: number;
  isVerified: boolean;
  interestedAt: string;
  // Backend fields
  averageRating: number | null;
  reviewCount: number;
  numberOfShifts: number | null;
  distanceMiles: number | null;
  isLiked: boolean;
}

/* ============================================
   QUERY PARAMETERS
   ============================================ */

export interface GetJobPostingsQueryParams {
  status?: JobPostingStatusEnum | null;
  pageNumber?: number;
  pageSize?: number;
}

/* ============================================
   ERROR RESPONSES
   ============================================ */

export interface ApiErrorResponse {
  error: string;
  errorCode?: string;
  statusCode?: number;
}
